import { computed } from 'vue';

import { useMessageContext } from './provider.js';
import { useMapGetter } from 'dashboard/composables/store';
import { ORIENTATION } from './constants';
import {
  groupParticipantName,
  groupRoster,
  isGroupConversation,
} from './groupParticipant.js';

/**
 * A mensagem é uma sincronização da ponte WAME cujo autor real não foi
 * identificado — ou seja, o `sender` gravado NÃO pode ser exibido como autor.
 *
 * Nesse caso o `sender` é o dono do token admin da empresa (o token é
 * compartilhado por toda a empresa; ver Messages::MessageBuilder#wame_author_sender),
 * não quem de fato enviou. Exibi-lo atribui a mensagem a um vendedor que não a
 * mandou — foi assim que um par de figurinhas vindas do celular passou a
 * aparecer como "Lucas" ao ser agrupado, enquanto solto mostrava o nome da
 * instância.
 *
 * Este predicado existe separado do nome porque cada bolha resolve a
 * APRESENTAÇÃO de um jeito (a do avatar devolve ícone e cai em "Atendente"
 * quando o inbox tem nome genérico; a do balão devolve só o texto). O que não
 * pode divergir é a DECISÃO — e era ela que estava duplicada em três lugares.
 *
 * @param {object} params
 * @param {object} params.contentAttributes  content_attributes da mensagem
 * @param {string} params.senderType         tipo do sender ("user", "contact"…)
 * @param {object} params.inbox              inbox da conversa
 * @returns {boolean}
 */
export function isUnattributedWameMessage({
  contentAttributes,
  senderType,
  inbox,
}) {
  const isApiInboxUserSender =
    inbox?.channel_type === 'Channel::Api' &&
    String(senderType || '').toLowerCase() === 'user';

  if (!isApiInboxUserSender) return false;

  const isWameSyncedMessage = !!(
    contentAttributes?.wameSync ?? contentAttributes?.wame_sync
  );
  const hasResolvedWameAuthor = !!(
    contentAttributes?.wameAuthorChatwootUserId ??
    contentAttributes?.wame_author_chatwoot_user_id
  );

  return isWameSyncedMessage && !hasResolvedWameAuthor;
}

/**
 * Nome do remetente exibido na bolha.
 *
 * Esta regra precisa existir num lugar só. Ela já vivia em Base.vue e em
 * Message.vue (avatarInfo); quando o álbum de stickers nasceu, ele desligou o
 * nome do BaseBubble (`hide-sender-name`) e reimplementou o rótulo lendo
 * `sender.name` cru — sem o guard do WAME. Resultado: um par de figurinhas
 * enviadas do celular aparecia como "Facundo" (nome do inbox) enquanto solto e
 * virava o nome do dono do token admin ao ser agrupado, reescrevendo a autoria
 * exibida e quebrando a auditoria de quem enviou.
 */
export function useDisplaySenderName() {
  const { sender, contentAttributes, orientation, inboxId, conversationId } =
    useMessageContext();

  const inboxGetter = useMapGetter('inboxes/getInbox');
  const conversationGetter = useMapGetter('getConversationById');

  return computed(() => {
    if (
      contentAttributes.value?.auroraAi ??
      contentAttributes.value?.aurora_ai
    ) {
      return 'IA';
    }

    const senderName = sender.value?.name;
    const senderType = String(sender.value?.type || '').toLowerCase();

    // Em grupo, `sender` é o CONTATO DO GRUPO — o mesmo para todo mundo que
    // fala ali. Usá-lo rotularia cada bolha com o nome do grupo e apagaria a
    // autoria, que é justamente o que distingue uma conversa de grupo. Quem
    // enviou está em content_attributes.
    // O gate é a CONVERSA, não a mensagem: assim nenhum atributo inesperado em
    // content_attributes consegue trocar o nome exibido numa conversa
    // individual, que precisa continuar se comportando exatamente como antes.
    const conversation = conversationGetter.value(conversationId.value);
    if (
      orientation.value === ORIENTATION.LEFT &&
      isGroupConversation(conversation)
    ) {
      const participant = groupParticipantName(
        contentAttributes.value,
        groupRoster(conversation)
      );
      if (participant) return participant;
    }

    // A mensagem recebida do WhatsApp pertence ao contato. Nao aplique o
    // fallback do atendente/inbox, pois isso trocaria o nome do cliente.
    if (orientation.value === ORIENTATION.LEFT && senderName) {
      return senderName;
    }

    const inbox = inboxGetter.value(inboxId.value) || {};
    const isApiInboxUserSender =
      inbox.channel_type === 'Channel::Api' && senderType === 'user';

    if (isApiInboxUserSender) {
      // Nunca usar o assignee ATUAL da conversa como substituto aqui — isso
      // reescreveria retroativamente a autoria exibida de mensagens antigas a
      // cada transferencia de responsavel. Cai no rotulo neutro do inbox.
      if (
        !isUnattributedWameMessage({
          contentAttributes: contentAttributes.value,
          senderType,
          inbox,
        })
      ) {
        return senderName;
      }
    } else if (senderName && senderType === 'user') {
      // Preserve the actual human sender when the message has one.
      return senderName;
    }

    if (inbox.channel_type === 'Channel::Api') {
      const inboxName = inbox.name || '';
      const nameFromInbox = inboxName.split('·')[0].trim();
      if (nameFromInbox) return nameFromInbox;
    }

    return senderName;
  });
}
